// src/pages/paidProject/Annexure 1/CostOfTheProject.jsx
import React, { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import API from "../../../api";

export default function CostOfTheProject() {
  const { project } = useOutletContext();
  const navigate = useNavigate();

  const [inputs, setInputs] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        const [inputsRes, plansRes] = await Promise.all([
          API.get(`/user/project-inputs/${project._id}`),
          API.get("/master/plans"),
        ]);

        if (!isMounted) return;
        setInputs(inputsRes.data);
        setPlans(plansRes.data || []);
      } catch (err) {
        console.error(err);
        if (isMounted) {
          setInputs(null);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [project._id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-gray-500">Loading…</span>
      </div>
    );
  }

  if (!inputs) {
    return <div className="p-6 text-red-500">Couldn’t load project costs.</div>;
  }

  const {
    plantMachineryData = [],
    plantContingencies = 0,
    plantGstPercent = 0,
    furnitureAndFixtures = [],
    otherFixedAssets = [],
    computers = [],
    preliminaryExpenses = [],
    costProjectInputs: costInputs = {},
    workingCapitalRequirementData: workingCapitalRequirement = [],
  } = inputs;

  // 1) Plant & Machinery calculations
  const pmSubtotal = plantMachineryData.reduce(
    (sum, row) => sum + (Number(row.amount) || 0),
    0
  );
  const pmContAmt = (pmSubtotal * plantContingencies) / 100;
  const pmTaxAmt = ((pmSubtotal + pmContAmt) * plantGstPercent) / 100;
  const plantMachineryGrandTotal = pmSubtotal + pmContAmt + pmTaxAmt;

  // 2) Other asset totals
  const furnitureTotal = furnitureAndFixtures.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );
  const miscFixedAssetsTotal = otherFixedAssets.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );
  const computerSoftwareTotal = computers.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );
  const preliminaryExpensesTotal = preliminaryExpenses.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );

  // 3) First‐year WCR
  const wcrFirstYear =
    (Array.isArray(workingCapitalRequirement)
      ? workingCapitalRequirement.find((v) => v > 0)
      : 0) || 0;

  // 4) Cost input fields
  const landDev = parseFloat(costInputs.landDevelopment) || 0;
  const civilConst = parseFloat(costInputs.civilConstruction) || 0;
  const deposits = parseFloat(costInputs.deposits) || 0;
  const termLoanPct = parseFloat(costInputs.termLoanPct) || 0;
  const equityPct = parseFloat(costInputs.equityPct) || 0;
  const wcPct = parseFloat(costInputs.workingCapitalPct) || 0;

  // 5) Project Cost (₹)
  const projectCost = [
    landDev,
    civilConst,
    plantMachineryGrandTotal,
    furnitureTotal,
    miscFixedAssetsTotal,
    computerSoftwareTotal,
    preliminaryExpensesTotal,
    wcrFirstYear,
    deposits,
  ].reduce((sum, v) => sum + v, 0);

  // Convert to lacs for plan range matching (1 lac = 1,00,000)
  const projectCostInLacs = projectCost / 100000;

  // 6) Means of Finance
  const workingCapitalTotal = Math.round((wcrFirstYear * wcPct) / 100);
  const termLoanTotal = Math.round(
    (projectCost - wcrFirstYear) * (termLoanPct / 100)
  );
  const equityTotal = projectCost - termLoanTotal - workingCapitalTotal;

  const formatAmt = (n) =>
    Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  // 7) Decide plan from actual project cost
  const selectedPlan =
    Array.isArray(plans) && plans.length
      ? plans.find(
          (p) =>
            projectCost >= (p.minCost || 0) &&
            projectCost <= (p.maxCost ?? Number.POSITIVE_INFINITY)
        )
      : null;

  const loadRzp = () =>
    new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.body.appendChild(s);
    });

  const handlePayNow = async () => {
    setPayError("");
    if (project.isPaid) return;
    if (!selectedPlan) {
      setPayError("No matching plan found for this project cost.");
      return;
    }

    const ok = await loadRzp();
    if (!ok) {
      setPayError("Could not load payment gateway.");
      return;
    }

    try {
      setPayLoading(true);
      const { data: order } = await API.post(
        `/user/projects/${project._id}/order`,
        { planId: selectedPlan._id }
      );

      const rzp = new window.Razorpay({
        key: order.key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: "YourAppName",
        description: "Project Plan Payment",
        prefill: { email: project.email, contact: project.phone },
        handler: async () => {
          try {
            await API.post(`/user/projects/${project._id}/pay`);
            navigate("/payment-success", { replace: true });
          } catch (e) {
            console.error(e);
            setPayError("Payment verification failed. Please contact support.");
          }
        },
        theme: { color: "#1976d2" },
      });

      rzp.open();
    } catch (err) {
      console.error(err);
      setPayError("Failed to initiate payment. Please try again.");
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div className="bg-white">
      {/* ─── Project Cost Table ───────────────────────────── */}
      <div className="overflow-x-auto mb-8 shadow-lg rounded-lg">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100">
            <tr className="h-8">
              <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                SL. NO.
              </th>
              <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                Particulars
              </th>
              <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                Amount (₹)
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              [1, "Land & Site Development (On Lease)", landDev],
              [2, "Civil Construction (On Lease)", civilConst],
              [3, "Plant & Machinery", plantMachineryGrandTotal],
              [4, "Furniture & Fixture", furnitureTotal],
              [5, "Miscellaneous Fixed Assets", miscFixedAssetsTotal],
              [6, "Computer & Software", computerSoftwareTotal],
              [7, "Preliminary Expenses", preliminaryExpensesTotal],
              [8, "Working Capital Requirement (Year 1)", wcrFirstYear],
              [9, "Deposits", deposits],
            ].map(([sl, label, value], idx) => (
              <tr key={idx} className={`even:bg-gray-50`}>
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800">
                  {sl}
                </td>
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800">
                  {label}
                </td>
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800 text-right">
                  {formatAmt(value)}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-200 font-bold">
              <td className="px-2 py-1 border border-gray-200" />
              <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800">
                Total
              </td>
              <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800 text-right">
                {formatAmt(projectCost)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Means of Finance Table ───────────────────────── */}
      <div className="overflow-x-auto mb-8 shadow-lg rounded-lg">
        <table className="min-w-full table-auto border-collapse">
          <thead className="bg-gray-100">
            <tr className="h-8">
              <th className="px-2 py-1 border border-gray-200 text-left font-medium text-sm">
                Means of Finance
              </th>
              <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                Rate (%)
              </th>
              <th className="px-2 py-1 border border-gray-200 text-right font-medium text-sm">
                Total (₹)
              </th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Term Loan", termLoanPct, termLoanTotal],
              ["Promoter’s Equity", equityPct, equityTotal],
              ["Working Capital", wcPct, workingCapitalTotal],
            ].map(([label, pct, total], idx) => (
              <tr key={idx} className={`even:bg-gray-50`}>
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800">
                  {label}
                </td>
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800 text-right">
                  {pct.toFixed(2)}
                </td>
                <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800 text-right">
                  {formatAmt(total)}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-200 font-bold">
              <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800">
                Total
              </td>
              <td className="px-2 py-1 border border-gray-200" />
              <td className="px-2 py-1 border border-gray-200 text-sm text-gray-800 text-right">
                {formatAmt(termLoanTotal + equityTotal + workingCapitalTotal)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Cost Summary, Plan & Payment / Download ───────────── */}
      <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
        <div className="flex flex-col text-sm text-gray-700">
          <span>
            Project Cost (computed): ₹{formatAmt(projectCost)} (
            {projectCostInLacs.toFixed(2)} lacs)
          </span>
          {selectedPlan ? (
            <span>
              Selected Plan:&nbsp;
              <span className="font-semibold">{selectedPlan.name}</span> – ₹
              {formatAmt(selectedPlan.price)} (range {selectedPlan.minCost}–
              {selectedPlan.maxCost} lacs)
            </span>
          ) : (
            <span className="text-red-500">
              No plan available for this project cost. Please contact support.
            </span>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {!project.isPaid && (
            <button
              onClick={handlePayNow}
              disabled={!selectedPlan || payLoading}
              className={`px-3 py-1 rounded text-white text-sm ${
                !selectedPlan || payLoading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {payLoading ? "Processing…" : "Pay Now"}
            </button>
          )}

          {project.isPaid ? (
            <button
              onClick={() =>
                navigate(`/paidProject/${project._id}/download-report`)
              }
              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm transition"
            >
              Download Report
            </button>
          ) : (
            <span className="text-gray-500 text-xs">
              Payment is required before download.
            </span>
          )}
        </div>
      </div>

      {payError && (
        <div className="mt-2 text-right text-xs text-red-500">{payError}</div>
      )}
    </div>
  );
}
